import React, { useState, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Link, useLocation, useParams } from 'react-router-dom';
import { connect } from 'react-redux';
import { 
    ArrowLeft, 
    Monitor, 
    Save, 
    Hash, 
    Settings, 
    Shield, 
    ShieldCheck, 
    ShieldX,
    User,
    Tag,
    AlertTriangle,
    CheckCircle,
    Plus
} from 'lucide-react';
import {registerVendingMachine, updateRegisteredVendingMachine,
    getRegisteredVendingMachine, getAvailableVendingMachines} from '../../actions/vendingMachine';

const initialState = {
    serialNumber: '',
    authorised: false,
    vendingMachine:'',
    vendingMachineId: '',
    name:'',
    version:''
};

const RegistrationDetails = ({
    getAvailableVendingMachines,
    getRegisteredVendingMachine,
    updateRegisteredVendingMachine,
    registerVendingMachine,
    loading,
    registeredVendingMachine,
    vendingMachinesForRegistration
}) => {
    const { id } = useParams();
    const [formData, setFormData] = useState(initialState);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState({});
    const { state } = useLocation();

    useEffect(() => {
        document.title = 'Registration Details';
        if(id !== 'new'){
            if (!registeredVendingMachine || (registeredVendingMachine.serialNumber !== id)){
                getRegisteredVendingMachine(id);
            } 
            const vendingMachineDetails = { ...initialState };
            if (registeredVendingMachine) {
                for (const key in registeredVendingMachine) {
                    if (key in vendingMachineDetails) vendingMachineDetails[key] = registeredVendingMachine[key];
                }
                setFormData(vendingMachineDetails);
            }
        }else{
            // For new registrations, initialize with state values
            getAvailableVendingMachines();
            if(state?.serialNumber || state?.version) {
                setFormData(prev => ({
                    ...prev,
                    serialNumber: state.serialNumber || '',
                    version: state.version || ''
                }));
            }
        }
    }, [registeredVendingMachine, state]);
    
    
    const {
        serialNumber,
        authorised,
        vendingMachine,
        vendingMachineId,
        name,
        version
    } = formData;
    
    const isNewRegistration = (id === 'new');

    const onChange = (e) => {
        const { name: fieldName, value } = e.target;
        
        // Clear error when user starts typing
        if (errors[fieldName]) {
            setErrors(prev => ({ ...prev, [fieldName]: '' }));
        }

        if(fieldName === "vendingMachine"){
            let registrationData = { ...formData}
            for (const vendingMachine of vendingMachinesForRegistration) {
                if(vendingMachine._id === value){
                    registrationData["vendingMachineId"] = vendingMachine.vendingMachineId;
                    registrationData["name"] = vendingMachine.name
                    registrationData["vendingMachine"] = vendingMachine._id;
                    // Always preserve state values for new registrations
                    if(isNewRegistration && state){
                        registrationData.version = state.version || '';
                        registrationData.serialNumber = state.serialNumber || '';
                    }
                    break;
                }
            }
            setFormData(registrationData);
        }else{
            setFormData({ ...formData, [fieldName]: value });
        }
    }

    const validateForm = () => {
        const newErrors = {};
        
        if (isNewRegistration && !formData.vendingMachine) {
            newErrors.vendingMachine = 'Please select a vending machine';
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const onSubmit = async e => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);
        try {
            if(isNewRegistration){
                await registerVendingMachine(formData);
            } else {
                await updateRegisteredVendingMachine({
                    _id: registeredVendingMachine._id, 
                    authorised: formData.authorised
                });
            }
        } catch (error) {
            console.error('Registration failed:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const showVendingMachines = () => {
        if (!vendingMachinesForRegistration || !Array.isArray(vendingMachinesForRegistration)) {
            return <option value="">No vending machines available</option>;
        }
        
        return vendingMachinesForRegistration.map((vendingMachine, index) => (
            <option key={`vm-${index}`} value={vendingMachine._id}>
                {vendingMachine.name}
            </option>
        ));
    }

    const getAuthorizationIcon = (authorized) => {
        if (authorized === 'true' || authorized === true) {
            return <ShieldCheck size={16} className="text-success me-2" />;
        }
        return <ShieldX size={16} className="text-danger me-2" />;
    };

    const getAuthorizationBadge = (authorized) => {
        if (authorized === 'true' || authorized === true) {
            return (
                <span className="badge bg-success d-flex align-items-center w-fit">
                    <ShieldCheck size={14} className="me-1" />
                    Authorized
                </span>
            );
        }
        return (
            <span className="badge bg-danger d-flex align-items-center w-fit">
                <ShieldX size={14} className="me-1" />
                Unauthorized
            </span>
        );
    };

    return (
        <Fragment>
            <div className="container-fluid py-4">
                {/* Header Section */}
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center flex-wrap">
                            <div>
                                <Link 
                                    className="btn btn-outline-primary d-flex align-items-center mb-2" 
                                    to={isNewRegistration ? '/inventory/v1.2/register-vending-machine' : '/inventory/v1.2/list-registered-vending-machine'}
                                >
                                    <ArrowLeft size={16} className="me-2" />
                                    Back to {isNewRegistration ? 'Registration Requests' : 'Registered Machines'}
                                </Link>
                                <h1 className="h3 mb-2 text-gray-800 fw-bold d-flex align-items-center">
                                    <Monitor size={32} className="me-3 text-primary" />
                                    {isNewRegistration ? 'Register New Machine' : 'Update Registration'}
                                </h1>
                                <p className="text-muted mb-0">
                                    {isNewRegistration ? 
                                        'Complete the registration for a new vending machine' :
                                        'Update the authorization status for this machine'
                                    }
                                </p>
                            </div>
                            <div className="d-flex align-items-center">
                                {!isNewRegistration && getAuthorizationBadge(authorised)}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="row justify-content-center">
                    <div className="col-lg-8">
                        {/* Registration Form Card */}
                        <div className="card shadow-sm">
                            <div className="card-header bg-primary text-white">
                                <h5 className="mb-0 d-flex align-items-center">
                                    <Settings size={20} className="me-2" />
                                    {isNewRegistration ? 'Registration Details' : 'Update Authorization'}
                                </h5>
                            </div>
                            <div className="card-body">
                                <form onSubmit={onSubmit}>
                                    <div className="row g-4">
                                        {/* Serial Number */}
                                        <div className="col-md-6">
                                            <label className="form-label fw-bold">
                                                <Hash size={16} className="me-2 text-primary" />
                                                Serial Number
                                            </label>
                                            <input
                                                className="form-control"
                                                type="text"
                                                name="serialNumber"
                                                value={isNewRegistration ? (state?.serialNumber || formData.serialNumber || '') : serialNumber}
                                                readOnly
                                                style={{backgroundColor: '#f8f9fa'}}
                                            />
                                        </div>

                                        {/* Version */}
                                        <div className="col-md-6">
                                            <label className="form-label fw-bold">
                                                <Tag size={16} className="me-2 text-info" />
                                                Version
                                            </label>
                                            <input
                                                className="form-control"
                                                type="text"
                                                name="version"
                                                value={isNewRegistration ? (state?.version || formData.version || '') : version}
                                                readOnly
                                                style={{backgroundColor: '#f8f9fa'}}
                                            />
                                        </div>

                                        {/* Authorization Status */}
                                        <div className="col-md-6">
                                            <label className="form-label fw-bold">
                                                <Shield size={16} className="me-2 text-warning" />
                                                Authorization Status
                                            </label>
                                            <select
                                                className="form-select"
                                                value={authorised}
                                                name="authorised"
                                                onChange={onChange}
                                            >
                                                <option value="true">
                                                    🟢 Authorize - Grant access
                                                </option>
                                                <option value="false">
                                                    🔴 Unauthorize - Deny access
                                                </option>
                                            </select>
                                            <div className="mt-2">
                                                {getAuthorizationBadge(authorised)}
                                            </div>
                                        </div>

                                        {/* Vending Machine Selection (only for new registrations) */}
                                        {isNewRegistration && (
                                            <div className="col-md-6">
                                                <label className="form-label fw-bold">
                                                    <Monitor size={16} className="me-2 text-success" />
                                                    Vending Machine *
                                                </label>
                                                <select
                                                    className={`form-select ${errors.vendingMachine ? 'is-invalid' : ''}`}
                                                    value={vendingMachine}
                                                    name="vendingMachine"
                                                    onChange={onChange}
                                                >
                                                    <option value="">Select Vending Machine</option>
                                                    {showVendingMachines()}
                                                </select>
                                                {errors.vendingMachine && (
                                                    <div className="invalid-feedback">
                                                        {errors.vendingMachine}
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* Machine ID (auto-populated) */}
                                        {vendingMachineId && (
                                            <div className="col-md-6">
                                                <label className="form-label fw-bold">
                                                    <Settings size={16} className="me-2 text-secondary" />
                                                    Machine ID
                                                </label>
                                                <input
                                                    className="form-control"
                                                    type="text"
                                                    name="vendingMachineId"
                                                    value={vendingMachineId}
                                                    readOnly
                                                    style={{backgroundColor: '#f8f9fa'}}
                                                />
                                            </div>
                                        )}

                                        {/* Machine Name (auto-populated) */}
                                        {name && (
                                            <div className="col-md-6">
                                                <label className="form-label fw-bold">
                                                    <User size={16} className="me-2 text-secondary" />
                                                    Machine Name
                                                </label>
                                                <input
                                                    className="form-control"
                                                    type="text"
                                                    name="name"
                                                    value={name}
                                                    readOnly
                                                    style={{backgroundColor: '#f8f9fa'}}
                                                />
                                            </div>
                                        )}
                                    </div>

                                    {/* Submit Button */}
                                    <div className="mt-5 text-center">
                                        <button 
                                            type="submit" 
                                            className="btn btn-primary btn-lg d-flex align-items-center mx-auto"
                                            disabled={isSubmitting}
                                        >
                                            {isSubmitting ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2"></span>
                                                    {isNewRegistration ? 'Registering...' : 'Updating...'}
                                                </>
                                            ) : (
                                                <>
                                                    {isNewRegistration ? (
                                                        <Plus size={16} className="me-2" />
                                                    ) : (
                                                        <Save size={16} className="me-2" />
                                                    )}
                                                    {isNewRegistration ? 'Register Machine' : 'Update Authorization'}
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>

                        {/* Info Card */}
                        {isNewRegistration && (
                            <div className="card shadow-sm mt-4">
                                <div className="card-body">
                                    <div className="d-flex align-items-start">
                                        <AlertTriangle size={20} className="text-warning me-3 mt-1" />
                                        <div>
                                            <h6 className="fw-bold text-warning mb-2">Registration Information</h6>
                                            <ul className="text-muted mb-0 small">
                                                <li>Select a vending machine to associate with this serial number</li>
                                                <li>Authorization status controls whether the machine can operate</li>
                                                <li>Machine ID and Name will be auto-populated based on your selection</li>
                                                <li>This action will complete the registration process</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </Fragment>
    );
}

RegistrationDetails.propTypes = {
    registerVendingMachine: PropTypes.func.isRequired,
    updateRegisteredVendingMachine: PropTypes.func.isRequired,
    getRegisteredVendingMachine: PropTypes.func.isRequired,    
    getAvailableVendingMachines: PropTypes.func.isRequired,
    loading: PropTypes.bool.isRequired,
    vendingMachinesForRegistration: PropTypes.array,
    registeredVendingMachine: PropTypes.object
};

const mapStateToProps = state => ({
    loading: state.product.loading,
    registeredVendingMachine: state.vendingMachine.registeredVendingMachine,
    vendingMachinesForRegistration: state.vendingMachine.vendingMachinesForRegistration
});

export default connect(
    mapStateToProps, 
    { registerVendingMachine, updateRegisteredVendingMachine, getRegisteredVendingMachine, getAvailableVendingMachines}
)(RegistrationDetails);
